import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import OptimizedButton from "../../../components/agent/UX/OptimizedButton";
import LoadingSpinner from "../../../components/agent/UX/LoadingSpinner";
import CreateTripModal from "../../../components/agent/trips/CreateTripModal";
import PixieDustAnimation from "../../../components/agent/PixieDustAnimation";
import usePixieDustNavigation from "../../../hooks/usePixieDustNavigation";
import { cleanupOnNavigation } from "../../../utils/tabCleanup";
import useErrorHandler from "../../../hooks/useErrorHandler";
import useOptimizedFetch from "../../../hooks/useOptimizedFetch";
import {
  Calendar,
  Users,
  TrendingUp,
  Sparkles,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  FileText,
  Info,
  ArrowUpRight,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import "../../../components/agent/crm/CRMDashboard.css";
import toast from "../../../utils/toast";
import formatDate from "../../../utils/formatDate";
import { getTripStatusInfo } from "../../../config/statusConfig";
import BaseModal from "../../../components/ui/BaseModal";
import { DashboardSkeleton } from "../../../components/ui/Skeleton";
import { useUser } from "../../../context/UserContext";
import UsageDashboard from "../../../components/agent/UsageDashboard";
import useItineraryLimits from "../../../hooks/useItineraryLimits";
import { decodeHtmlEntities } from "../../../utils/textUtils";

export default function UnifiedDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeWithErrorHandling } = useErrorHandler();
  const [showPendingTripModal, setShowPendingTripModal] = useState(false);
  const [selectedPendingTrip, setSelectedPendingTrip] = useState(null);
  const [showCreateTripModal, setShowCreateTripModal] = useState(false);

  // Pixie dust navigation hook
  const {
    showAnimation,
    animationDuration,
    navigateToUnifiedItinerary,
    handleAnimationComplete
  } = usePixieDustNavigation();

  const { user, loadingUser, usageSummary, loadingUsage } = useUser();
  // Itinerary limits and recommendations
  const {
    usage: limitUsage,
    canCreate: canCreateItinerary,
    recommendations,
    loading: limitsLoading
  } = useItineraryLimits();

  // Data fetching — uses mock data when VITE_DEV_MOCK_USER is set, otherwise real API
  const isDevMock = import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER;

  const {
    data: apiTrips,
    loading: tripsLoading,
    error: tripsError,
    refetch: refetchTrips
  } = useOptimizedFetch('/api/trips', {
    cacheTTL: 2 * 60 * 1000,
    dependencies: [user?.userid],
    enabled: !loadingUser && !!user?.userid && !isDevMock
  });

  const {
    data: apiClients,
    loading: clientsLoading,
    error: clientsError,
    refetch: refetchClients
  } = useOptimizedFetch('/api/clients', {
    cacheTTL: 5 * 60 * 1000,
    dependencies: [user?.userid],
    enabled: !loadingUser && !!user?.userid && !isDevMock
  });

  // In mock mode, load dev data
  const [mockTrips, setMockTrips] = useState(null);
  const [mockClients, setMockClients] = useState(null);
  useEffect(() => {
    if (isDevMock) {
      import('../../../dev/mockCrmTrips.js').then(({ getDevMockDashboardData }) => {
        const { trips: t, clients: c } = getDevMockDashboardData();
        setMockTrips(t);
        setMockClients(c);
      });
    }
  }, [isDevMock]);

  const trips = isDevMock ? mockTrips : apiTrips;
  const clients = isDevMock ? mockClients : apiClients;
  const loading = loadingUser || (isDevMock ? !mockTrips : (tripsLoading || clientsLoading));

  // Handle errors from optimized fetch
  useEffect(() => {
    if (tripsError) {
      executeWithErrorHandling(() => {
        throw tripsError;
      });
    }
  }, [tripsError, executeWithErrorHandling]);

  useEffect(() => {
    if (clientsError) {
      executeWithErrorHandling(() => {
        throw clientsError;
      });
    }
  }, [clientsError, executeWithErrorHandling]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    
    // Clean up tabs when navigating to dashboard
    if (location.pathname === '/dashboard') {
      cleanupOnNavigation();
    }
  }, [location.pathname]);

  // Calculate dashboard stats with proper field names
  const totalTrips = useMemo(() => trips?.length || 0, [trips]);
  const completedTrips = useMemo(() => trips?.filter(
    (trip) => trip.status === "completed"
  ).length || 0, [trips]);
  const pendingTrips = useMemo(() => trips?.filter((trip) => trip.status === "pending").length || 0, [trips]);
  const confirmedTrips = useMemo(() => trips?.filter((trip) => trip.status === "confirmed").length || 0, [trips]);

  // Sort trips by creation date (most recent first) and take first 3
  const recentTrips = useMemo(() => trips?.slice().sort((a, b) => {
    const dateA = new Date(a.createdat || 0);
    const dateB = new Date(b.createdat || 0);
    return dateB - dateA;
  }).slice(0, 5) || [], [trips]);

  // Get unique clients from trips
  const uniqueClients = useMemo(() => new Set(trips?.map(trip => trip.clientid) || []).size, [trips]);
  const totalClients = useMemo(() => clients?.length || 0, [clients]);
  const activeLeads = useMemo(() => clients?.filter(c => c.relationship_status === 'lead').length || 0, [clients]);
  const prospects = useMemo(() => clients?.filter(c => c.relationship_status === 'prospect').length || 0, [clients]);
  const customers = useMemo(() => clients?.filter(c => c.relationship_status === 'customer').length || 0, [clients]);


  const convertedThisMonth = useMemo(() => clients?.filter(c =>
    c.relationship_status === 'customer' &&
    new Date(c.createdat) >= new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  ).length || 0, [clients]);

  const conversionRate = useMemo(() => totalClients > 0 ? ((customers / totalClients) * 100) : 0, [customers, totalClients]);

  // Month-over-month trend computation
  const trends = useMemo(() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    const createdInRange = (items, start, end) =>
      (items || []).filter(item => {
        const d = new Date(item.createdat || 0);
        return d >= start && d < end;
      }).length;

    const clientsThisMonth = createdInRange(clients, thisMonthStart, now);
    const clientsLastMonth = createdInRange(clients, lastMonthStart, thisMonthStart);
    const tripsThisMonth = createdInRange(trips, thisMonthStart, now);
    const tripsLastMonth = createdInRange(trips, lastMonthStart, thisMonthStart);

    const trendFor = (current, previous) => {
      if (previous === 0 && current === 0) return null;
      if (previous === 0) return { direction: 'up', label: `+${current} this month` };
      const pct = Math.round(((current - previous) / previous) * 100);
      if (pct > 0) return { direction: 'up', label: `+${pct}% vs last month` };
      if (pct < 0) return { direction: 'down', label: `${pct}% vs last month` };
      return { direction: 'flat', label: 'Same as last month' };
    };

    return {
      clients: trendFor(clientsThisMonth, clientsLastMonth),
      trips: trendFor(tripsThisMonth, tripsLastMonth),
    };
  }, [clients, trips]);

  // Build a rich activity feed from recent clients and trips (up to 8 items)
  const sortedActivities = useMemo(() => {
    const activities = [];

    // Add recent clients (up to 4)
    if (clients?.length) {
      clients.slice().sort((a, b) => new Date(b.createdat || 0) - new Date(a.createdat || 0))
        .slice(0, 4)
        .forEach(c => {
          if (!c.createdat) return;
          activities.push({
            icon: CheckCircle, iconColor: 'green',
            message: `Client added: ${c.fullname || c.FullName || 'Unknown'}`,
            timestamp: formatDate(c.createdat, { format: 'relative' }),
            date: new Date(c.createdat),
          });
        });
    }

    // Add recent trips (up to 4)
    if (trips?.length) {
      trips.slice().sort((a, b) => new Date(b.createdat || 0) - new Date(a.createdat || 0))
        .slice(0, 4)
        .forEach(t => {
          if (!t.createdat) return;
          const statusLabel = t.status?.toLowerCase() === 'completed' ? 'completed'
            : t.status?.toLowerCase() === 'confirmed' ? 'confirmed'
            : 'created';
          activities.push({
            icon: Calendar, iconColor: 'blue',
            message: `Trip ${statusLabel}: ${decodeHtmlEntities(t.tripname) || 'Unnamed trip'}`,
            timestamp: formatDate(t.createdat, { format: 'relative' }),
            date: new Date(t.createdat),
          });
        });
    }

    return activities.sort((a, b) => b.date - a.date).slice(0, 8);
  }, [clients, trips]);

  // Simple flags to detect whether the agent has any meaningful activity yet
  const hasAnyTrips = totalTrips > 0;
  const hasAnyClients = totalClients > 0;
  const hasMeaningfulActivity = hasAnyTrips || hasAnyClients;

  // Needs Attention items — computed from existing data
  const attentionItems = useMemo(() => {
    const items = [];
    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;

    // Trips stuck in "pending" > 3 days or > 7 days
    if (trips?.length) {
      trips.forEach(trip => {
        if (trip.status?.toLowerCase() !== 'pending') return;
        const created = new Date(trip.createdat || 0).getTime();
        if (!created) return;
        const daysOld = Math.floor((now - created) / DAY_MS);
        if (daysOld >= 7) {
          items.push({
            icon: AlertCircle,
            title: `"${decodeHtmlEntities(trip.tripname) || 'Unnamed trip'}" — pending for ${daysOld} days`,
            description: 'Client has not completed their questionnaire. Consider following up.',
            href: '/agent/crm/trips',
            urgent: true,
          });
        } else if (daysOld >= 3) {
          items.push({
            icon: Clock,
            title: `"${decodeHtmlEntities(trip.tripname) || 'Unnamed trip'}" — pending for ${daysOld} days`,
            description: 'Questionnaire not yet completed. You may want to send a reminder.',
            href: '/agent/crm/trips',
            urgent: false,
          });
        }
      });
    }

    // Clients with no trips > 5 days old
    if (clients?.length && trips) {
      const clientIdsWithTrips = new Set((trips || []).map(t => t.clientid));
      clients.forEach(client => {
        if (clientIdsWithTrips.has(client.clientid)) return;
        const created = new Date(client.createdat || 0).getTime();
        if (!created) return;
        const daysOld = Math.floor((now - created) / DAY_MS);
        if (daysOld >= 5) {
          items.push({
            icon: Users,
            title: `${client.fullname || client.FullName || 'Unnamed client'} — no trips yet`,
            description: `Added ${daysOld} days ago. Consider creating a trip for them.`,
            href: '/agent/crm/clients',
            urgent: daysOld >= 10,
          });
        }
      });
    }

    // Sort: urgent first, then by recency
    return items.sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0)).slice(0, 6);
  }, [trips, clients]);

  // Status info from shared config
  const getStatusForTrip = useCallback((status) => getTripStatusInfo(status), []);

  // Handle trip click based on status
  const handleTripClick = useCallback((trip) => {
    if (trip.status?.toLowerCase() === "pending") {
      setSelectedPendingTrip(trip);
      setShowPendingTripModal(true);
    } else {
      navigateToUnifiedItinerary(trip.tripid);
    }
  }, [navigateToUnifiedItinerary]);


  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  // CRM-style metric cards — only cards backed by real data
  const crmMetricCards = [
    {
      title: "Total Clients",
      value: (totalClients || 0).toLocaleString(),
      icon: Users,
      color: "blue",
      subtitle:
        totalClients && (activeLeads || prospects)
          ? `${activeLeads} leads, ${prospects} prospects`
          : "Add your first client to get started",
      trend: trends.clients,
    },
    {
      title: "Active Trips",
      value: (totalTrips || 0).toLocaleString(),
      icon: Calendar,
      color: "green",
      subtitle: `${pendingTrips} pending, ${completedTrips} completed`,
      trend: trends.trips,
    },
    {
      title: "Conversion Rate",
      value: `${(typeof conversionRate === 'number' ? conversionRate : parseFloat(conversionRate) || 0).toFixed(1)}%`,
      icon: TrendingUp,
      color: "orange",
      subtitle: `${customers} customers, ${convertedThisMonth} this month`,
    },
  ];


  // Show loading state while user is being loaded
  // Note: Topbar + SidebarV2 are already provided by AppChrome > AgentShell
  if (loadingUser) {
    return <DashboardSkeleton />;
  }

  return (
    <>
      <div className="w-full">
        <div className="mx-auto max-w-7xl">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="crm-main-content"
            >
            {/* Welcome Section with Itinerary Usage Counter Side-by-Side */}
            <motion.div variants={itemVariants} className="mb-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                {/* Welcome Message - Left Side */}
                <div className="flex-1">
                  <h1 className="mb-1 text-xl font-bold text-gray-900 sm:text-2xl">
                    Welcome back, {user?.firstname || "Agent"}!
                  </h1>
                  <p className="text-sm text-gray-600">
                    Manage your Disney World trips and create magical experiences for your clients
                  </p>
                </div>
                
                {/* Itinerary Usage Counter - Right Side */}
                <div className="flex-shrink-0 w-full lg:w-80 xl:w-96">
                  <UsageDashboard usageSummary={usageSummary} loadingUsage={loadingUsage} />
                </div>
              </div>
            </motion.div>

            {/* Upgrade Notification Banner */}
            {recommendations && recommendations.should_upgrade && (
              <motion.div variants={itemVariants} className="mb-6">
                <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {canCreateItinerary ? 'Approaching Your Limit' : 'Monthly Limit Reached'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {recommendations.reason}. {canCreateItinerary ? 'Consider upgrading' : 'Upgrade'} to {recommendations.recommended_plan} plan to continue creating itineraries.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/agent/settings?tab=subscription')}
                      className="px-4 py-2 ml-4 text-sm font-medium text-white whitespace-nowrap bg-orange-600 rounded-lg transition-colors hover:bg-orange-700"
                    >
                      Upgrade Now
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Limit Reached Banner (if can't create) */}
            {!canCreateItinerary && !limitsLoading && (
              <motion.div variants={itemVariants} className="mb-6">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Monthly Itinerary Limit Reached
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          You've used all {limitUsage?.monthly_limit} itineraries for this month. Upgrade your plan to create more itineraries.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/agent/settings?tab=subscription')}
                      className="px-4 py-2 ml-4 text-sm font-medium text-white whitespace-nowrap bg-red-600 rounded-lg transition-colors hover:bg-red-700"
                    >
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

              {/* First-Run Welcome Experience (no data yet) */}
              {!hasMeaningfulActivity && !loading && (
                <motion.div variants={itemVariants} className="mb-6">
                  <div className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="mx-auto max-w-2xl text-center">
                      <div className="flex justify-center items-center mx-auto mb-4 w-14 h-14 bg-blue-100 rounded-full">
                        <Sparkles className="w-7 h-7 text-blue-600" />
                      </div>
                      <h2 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">
                        Let's get started, {user?.firstname || "Agent"}!
                      </h2>
                      <p className="mb-8 text-sm text-gray-600 sm:text-base">
                        Set up your first trip in three simple steps. Your dashboard metrics and activity will appear here as you start working.
                      </p>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <button
                          onClick={() => navigate("/agent/crm/clients")}
                          className="p-5 text-left bg-gray-50 rounded-lg border border-gray-200 transition-all hover:border-blue-300 hover:shadow-sm"
                          type="button"
                        >
                          <div className="flex justify-center items-center mb-3 w-9 h-9 bg-blue-100 rounded-lg">
                            <Users className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="mb-1 text-sm font-semibold text-gray-900">1. Add a client</div>
                          <p className="text-xs text-gray-500">Enter your client's name and contact info</p>
                        </button>
                        <button
                          onClick={() => setShowCreateTripModal(true)}
                          className="p-5 text-left bg-gray-50 rounded-lg border border-gray-200 transition-all hover:border-blue-300 hover:shadow-sm"
                          type="button"
                        >
                          <div className="flex justify-center items-center mb-3 w-9 h-9 bg-green-100 rounded-lg">
                            <Calendar className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="mb-1 text-sm font-semibold text-gray-900">2. Create a trip</div>
                          <p className="text-xs text-gray-500">Pick a destination and send the questionnaire</p>
                        </button>
                        <button
                          onClick={() => navigate("/agent/crm/trips")}
                          className="p-5 text-left bg-gray-50 rounded-lg border border-gray-200 transition-all hover:border-blue-300 hover:shadow-sm"
                          type="button"
                        >
                          <div className="flex justify-center items-center mb-3 w-9 h-9 bg-purple-100 rounded-lg">
                            <FileText className="w-4 h-4 text-purple-600" />
                          </div>
                          <div className="mb-1 text-sm font-semibold text-gray-900">3. View itinerary</div>
                          <p className="text-xs text-gray-500">View your clients automated itinerary</p>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* CRM Metrics Grid — only shown when there's real data */}
              {hasMeaningfulActivity && (
              <motion.div variants={itemVariants} className="mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-6">
                  {crmMetricCards.map((metric) => {
                    const colorMap = {
                      blue: { bg: "bg-blue-50", text: "text-blue-600" },
                      green: { bg: "bg-green-50", text: "text-green-600" },
                      orange: { bg: "bg-orange-50", text: "text-orange-600" },
                    };
                    const colors = colorMap[metric.color] || colorMap.blue;
                    return (
                      <motion.div
                        key={metric.title}
                        variants={itemVariants}
                        className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 hover:shadow-md"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className={`flex justify-center items-center w-8 h-8 ${colors.bg} rounded-lg`}>
                            <metric.icon size={16} className={colors.text} />
                          </div>
                          {metric.trend && (
                            <span className={`flex items-center gap-0.5 text-[10px] font-medium ${
                              metric.trend.direction === 'up' ? 'text-green-600' :
                              metric.trend.direction === 'down' ? 'text-red-500' :
                              'text-gray-400'
                            }`}>
                              {metric.trend.direction === 'up' && <span>&#9650;</span>}
                              {metric.trend.direction === 'down' && <span>&#9660;</span>}
                              {metric.trend.direction === 'flat' && <span>&ndash;</span>}
                              {metric.trend.label}
                            </span>
                          )}
                        </div>
                        <div className="mb-1 text-xl font-bold text-gray-900">{metric.value}</div>
                        <div className="mb-1 text-xs font-medium text-gray-600">{metric.title}</div>
                        <div className="text-xs text-gray-500">{metric.subtitle}</div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
              )}

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 md:gap-6">
                {/* Recent Trips */}
                <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Trips</h3>
                    <button
                      onClick={() => navigate("/agent/crm/trips")}
                      className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                    >
                      View All
                      <ArrowUpRight size={16} />
                    </button>
                  </div>
                  <div className="p-6">
                    {loading ? (
                      <div className="flex justify-center items-center py-6 sm:py-8">
                        <LoadingSpinner size="lg" text="Loading your trips..." />
                      </div>
                    ) : recentTrips.length === 0 ? (
                      <div className="py-6 text-center sm:py-8">
                        <div className="flex justify-center items-center mx-auto mb-2 w-10 h-10 bg-gray-100 rounded-full sm:w-12 sm:h-12">
                          <Calendar className="w-5 h-5 text-gray-400 sm:w-6 sm:h-6" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-gray-900 sm:text-base">No trips yet</h3>
                        <p className="mb-2 text-xs text-gray-600 sm:text-sm">Create your first Disney World adventure to get started!</p>
                        <button
                          onClick={() => navigate("/agent/crm/trips")}
                          className="inline-flex items-center px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 sm:text-sm"
                        >
                          <Plus className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                          Create Your First Trip
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {recentTrips.map((trip) => (
                          <button
                            key={trip.tripid}
                            className="flex justify-between items-center p-4 w-full text-left bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow cursor-pointer hover:shadow-md"
                            onClick={() => handleTripClick(trip)}
                            type="button"
                          >
                            <div className="flex flex-1 items-center space-x-3 min-w-0">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                trip.status?.toLowerCase() === "completed" 
                                  ? "bg-green-500" 
                                  : trip.status?.toLowerCase() === "pending"
                                  ? "bg-yellow-500"
                                  : "bg-gray-500"
                              }`} />
                              <div className="flex-1 min-w-0">
                                <h4 className="mb-1 text-base font-bold text-gray-900 truncate">
                                  {decodeHtmlEntities(trip.tripname)}
                                </h4>
                                <p className="text-sm text-gray-600 truncate">
                                  {decodeHtmlEntities(trip.fullname) || trip.clientemail || "Client"}
                                </p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-2 text-right">
                              {(() => {
                                const si = getStatusForTrip(trip.status);
                                return (
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${si.color}`}
                                    title={si.description}
                                  >
                                    {si.label}
                                  </span>
                                );
                              })()}
                              <p className="mt-1 text-xs text-gray-500">
                                {formatDate(trip.createdat)}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Recent Data Summary */}
                <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                    <button
                      onClick={() => navigate("/agent/crm/clients")}
                      className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                    >
                      View All
                      <ArrowUpRight size={16} />
                    </button>
                  </div>
                  <div className="p-6">
                    {!hasMeaningfulActivity ? (
                      <div className="py-6 text-center sm:py-8">
                        <div className="flex justify-center items-center mx-auto mb-3 w-10 h-10 bg-gray-100 rounded-full sm:w-12 sm:h-12">
                          <Sparkles className="w-5 h-5 text-gray-400 sm:w-6 sm:h-6" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-gray-900 sm:text-base">
                          No activity yet
                        </h3>
                        <p className="mx-auto mb-3 max-w-sm text-xs text-gray-600 sm:text-sm">
                          As you add clients, create trips, and start earning commissions, your recent activity timeline will appear here.
                        </p>
                        <div className="flex flex-col gap-2 items-center sm:flex-row sm:justify-center">
                          <button
                            onClick={() => navigate("/agent/crm/clients")}
                            className="inline-flex items-center px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 sm:text-sm"
                          >
                            <Users className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                            Add Your First Client
                          </button>
                          <button
                            onClick={() => navigate("/agent/crm/trips")}
                            className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100 sm:text-sm"
                          >
                            <Calendar className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                            Create Your First Trip
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {sortedActivities.length > 0 ? (
                          sortedActivities.map((activity, index) => {
                            const IconComponent = activity.icon;
                            const iconBgColor = activity.iconColor === 'green' ? 'bg-green-100' :
                                               activity.iconColor === 'blue' ? 'bg-blue-100' :
                                               activity.iconColor === 'purple' ? 'bg-purple-100' :
                                               'bg-orange-100';
                            const iconTextColor = activity.iconColor === 'green' ? 'text-green-600' :
                                                  activity.iconColor === 'blue' ? 'text-blue-600' :
                                                  activity.iconColor === 'purple' ? 'text-purple-600' :
                                                  'text-orange-600';
                            
                            return (
                              <div key={`${activity.type}-${activity.timestamp}`} className="flex items-center space-x-3">
                                <div className={`flex justify-center items-center w-8 h-8 rounded-full ${iconBgColor}`}>
                                  <IconComponent className={`w-4 h-4 ${iconTextColor}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {activity.message}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {activity.timestamp}
                                  </p>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          // Fallback to summary if no recent activities with dates
                          <>
                            {hasAnyClients && (
                              <div className="flex items-center space-x-3">
                                <div className="flex justify-center items-center w-8 h-8 bg-green-100 rounded-full">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {totalClients === 1 ? "1 client in your CRM" : `${totalClients} clients in your CRM`}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {activeLeads} leads, {prospects} prospects, {customers} customers
                                  </p>
                                </div>
                              </div>
                            )}

                            {hasAnyTrips && (
                              <div className="flex items-center space-x-3">
                                <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                                  <Calendar className="w-4 h-4 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {totalTrips === 1 ? "1 active trip in progress" : `${totalTrips} trips in your workspace`}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {pendingTrips} pending, {completedTrips} completed
                                  </p>
                                </div>
                              </div>
                            )}

                          </>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Needs Attention */}
              {attentionItems.length > 0 && (
              <motion.div variants={itemVariants} className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                  <div className="flex gap-2 items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Needs Attention</h3>
                    <span className="px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-100 rounded-full">
                      {attentionItems.length}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {attentionItems.map((item, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => item.href && navigate(item.href)}
                        className="flex gap-3 items-start p-3 w-full text-left rounded-lg border transition-colors hover:bg-gray-50"
                        style={{ borderColor: item.urgent ? '#fde68a' : '#e5e7eb' }}
                      >
                        <div className={`flex-shrink-0 p-1.5 rounded-lg ${item.urgent ? 'bg-amber-100' : 'bg-gray-100'}`}>
                          <item.icon className={`w-4 h-4 ${item.urgent ? 'text-amber-600' : 'text-gray-500'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{item.title}</p>
                          <p className="text-xs text-gray-500">{item.description}</p>
                        </div>
                        <ArrowUpRight className="flex-shrink-0 w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
              )}
            </motion.div>
        </div>
      </div>

      {/* Pending Trip Modal */}
      <BaseModal
        isOpen={showPendingTripModal && !!selectedPendingTrip}
        onClose={() => setShowPendingTripModal(false)}
        title="Trip Not Ready Yet"
        size="sm"
      >
        <BaseModal.Body>
          <p className="mb-3 text-sm text-gray-600">
            The trip "<strong>{selectedPendingTrip ? decodeHtmlEntities(selectedPendingTrip.tripname) : ''}</strong>" is still pending.
            Your client needs to complete the questionnaire before you can view the itinerary.
          </p>
          <div className="p-3 rounded-lg border border-yellow-200 bg-yellow-50">
            <p className="text-sm font-medium text-yellow-800">Next Steps:</p>
            <ul className="mt-2 space-y-1 text-sm text-yellow-700">
              <li className="flex"><span className="mr-2">1.</span><span>Share the questionnaire link with your client</span></li>
              <li className="flex"><span className="mr-2">2.</span><span>Wait for them to complete their preferences</span></li>
              <li className="flex"><span className="mr-2">3.</span><span>Once completed, the itinerary generates automatically and you'll be notified</span></li>
            </ul>
          </div>
        </BaseModal.Body>
        <BaseModal.Footer>
          <button
            onClick={() => setShowPendingTripModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
            type="button"
          >
            Got it
          </button>
          <button
            onClick={() => { setShowPendingTripModal(false); navigate("/agent/crm/trips"); }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            type="button"
          >
            Manage Trips
          </button>
        </BaseModal.Footer>
      </BaseModal>

      {/* Create Trip Modal */}
      {showCreateTripModal && (
        <CreateTripModal
          isOpen={showCreateTripModal}
          onClose={() => setShowCreateTripModal(false)}
          onSuccess={() => {
            setShowCreateTripModal(false);
            // Optionally refresh trips data or show success message
            toast.success("Trip created successfully!");
          }}
        />
      )}

      {/* Pixie Dust Animation */}
      {showAnimation && (
        <PixieDustAnimation
          onComplete={handleAnimationComplete}
          duration={animationDuration}
        />
      )}
    </>
  );
}
